import { shouldHighlightEmptyTomorrowCell } from "./highlightEmptyTomorrowCell";
import { shouldHighlightPartialCapacityCell } from "./shouldHighlightPartialCapacityCell";
import { datesEqual } from "./dateCompare";
import { OFF_ATTRIBUTE_VALUE } from "./installerHasFullWeekOff";

// - - - - - - - - - -
// #139 Phase 7 — Next Day Openings Dashboard aggregation helper.
//
// Given the raw Lambda responses fetched by useDashboardOpenings (one set
// per location), produce a per-location list of installers whose tomorrow
// cell would qualify for the same red highlight #138 already shows on the
// Optimizer grid.
//
// CRITICAL: the eligibility decision DELEGATES to
// shouldHighlightEmptyTomorrowCell (#138). The dashboard must agree with
// what the Optimizer's grid shows; both call the SAME helper to guarantee
// parity. The only work here is shaping raw Lambda responses into the
// scheduleArray-doc shape that shouldHighlightEmptyTomorrowCell expects.
//
// This module purposefully does NOT pull from ScheduleContext — it works
// on raw Lambda payloads so it stays pure + unit-testable. The trade-off
// is replicating two pieces of logic that exist in schedule.context.js:
//   1. Compliance-hold doc construction (schedule.context.js:1617-1647)
//   2. Default-availability doc materialization (schedule.context.js
//      :1355-1505, the weekDatesArray.forEach loop)
// Both are simple enough that drift risk is low and the tests in this
// module pin the behavior.
//
// The payrollNo formatting mirrors AppContext's `dataFormatting_payrollFields`
// — zero-pad to 7 chars total, with an extra trailing zero when the
// suffix is one digit (e.g. "3264.4" → "3264.40"). Extracted as a pure
// function so test fixtures can produce matching shapes without spinning
// up a context provider.

// - - - - - - - - - - shape helpers

/**
 * Mirror of `dataFormatting_payrollFields` in app.context.js. Pure.
 * Returns the canonical string form used everywhere in the app to compare
 * installer payrollNos.
 */
export function payrollFormat(raw) {
  if (raw === null || raw === undefined || raw === "") return "";
  const str = String(raw);
  const length = str.length;
  const decimalPos = str.indexOf(".");
  let normalized = str;
  if (decimalPos === length - 2) {
    // suffix has a single digit, append a "0" → "3264.4" → "3264.40"
    normalized = str + "0";
  }
  return normalized.padStart(7, "0");
}

/**
 * Extract the projects array from a getProjectsByCompany Lambda response.
 * Returns [] for missing / unknown shapes so callers never crash.
 */
export function extractProjects(rawPayload) {
  if (!rawPayload) return [];
  const result = rawPayload?.message?.result;
  if (Array.isArray(result?.projects)) return result.projects;
  if (Array.isArray(result)) return result;
  return [];
}

/**
 * Extract the candidates array from a getCandidatesByCompany Lambda response.
 * Handles both the new object format (`{ candidates: [...] }`) and the legacy
 * array-direct format.
 */
export function extractCandidates(rawPayload) {
  if (!rawPayload) return [];
  const result = rawPayload?.message?.result;
  if (Array.isArray(result?.candidates)) return result.candidates;
  if (Array.isArray(result)) return result;
  return [];
}

/**
 * Extract the attributes array from a getAttributesByLocation Lambda response.
 */
export function extractAttributes(rawPayload) {
  if (!rawPayload) return [];
  const result = rawPayload?.message?.result;
  if (Array.isArray(result)) return result;
  if (Array.isArray(result?.attributes)) return result.attributes;
  return [];
}

/**
 * Extract the `meta` object from a getProjectsByCompanyWithShared response.
 *
 * #168 — that Lambda swallows internal errors and returns
 * `{ projects: [], meta: { error } }` with HTTP 200. TanStack treats the
 * 200 as success (isError stays false), so the dashboard cannot see the
 * failure through query state alone — it would read the empty `projects`
 * array as "this location has zero booked jobs" and count EVERY idle
 * installer as an opening (a silent over-count, the #151 pattern).
 *
 * Reading `meta.error` here is the only signal that the projects payload is
 * degraded rather than genuinely empty. `isProjectsPayloadDegraded` below
 * uses it to exclude the location from the count.
 *
 * Returns null for missing / unknown shapes.
 */
export function extractProjectsMeta(rawPayload) {
  return rawPayload?.message?.result?.meta ?? null;
}

/**
 * True when a location's data cannot be trusted to compute openings, so the
 * location MUST be excluded from the count (never counted as "everyone open").
 *
 * Degraded means any of:
 *   - the hook flagged isError (a query threw / non-2xx)
 *   - a dataset never arrived (candidates / attributes / projects is nullish
 *     while NOT loading — i.e. an errored first fetch)
 *   - the projects Lambda returned its silent-error sentinel (`meta.error`),
 *     i.e. `projects: []` at HTTP 200 (see extractProjectsMeta)
 *
 * NOTE: isFetching is deliberately NOT degraded — during a refresh the prior
 * successful data is retained and remains trustworthy, so those openings
 * keep rendering (see the isFetching test).
 *
 * @returns {{degraded: boolean, reason: string|null}}
 */
export function assessLocationData(entry) {
  if (!entry) return { degraded: true, reason: "missing-entry" };
  if (entry.isError === true) return { degraded: true, reason: "query-error" };
  const projectsMeta = extractProjectsMeta(entry.projects);
  if (projectsMeta?.error) {
    return { degraded: true, reason: "projects-lambda-error" };
  }
  if (entry.candidates == null) return { degraded: true, reason: "no-candidates-data" };
  if (entry.attributes == null) return { degraded: true, reason: "no-attributes-data" };
  if (entry.projects == null) return { degraded: true, reason: "no-projects-data" };
  return { degraded: false, reason: null };
}

// - - - - - - - - - - scheduleArray construction

// Field-name keys used inside default-availability sub-objects. The "Thr"
// (not "Thu") is intentional — mirrors what schedule.context.js looks for
// when materializing defaults into per-weekday OFF docs (see lines
// 1382-1505 there).
const WEEKDAY_KEYS_BY_DAY_INDEX = [
  "Sun",
  "Mon",
  "Tue",
  "Wed",
  "Thr",
  "Fri",
  "Sat",
];

/**
 * Construct the minimum scheduleArray-shaped doc set needed by
 * shouldHighlightEmptyTomorrowCell for evaluating ONE date (tomorrow)
 * at ONE location.
 *
 * The doc shape mirrors what schedule.context.js's createScheduleArray
 * produces — job-tile docs (attribute !== true) and attribute docs
 * (attribute === true). We only construct docs covering tomorrow's date
 * for installers at this location; everything else is irrelevant.
 */
export function buildTomorrowScheduleArray({
  locationCode,
  candidates,
  attributes,
  projects,
  tomorrowDateString,
}) {
  if (!tomorrowDateString || !locationCode) return [];
  const tomorrowMs = new Date(tomorrowDateString).getTime();
  if (Number.isNaN(tomorrowMs)) return [];

  const tomorrowDayIndex = new Date(tomorrowDateString).getDay();
  const tomorrowWeekdayKey = WEEKDAY_KEYS_BY_DAY_INDEX[tomorrowDayIndex];

  const scheduleArray = [];

  // 1. Job-tile docs — projects where (statusCode !== 20 cancelled) AND
  //    date range [date_scheduled..date_estimatedCompletion] covers
  //    tomorrow. Per #138, the location filter is intentionally dropped
  //    here so shared-installer cross-location assignments still count
  //    as booked.
  //
  //    #161 (2026-05-26, Brandon): originally this filter required
  //    `statusCode === 4` strict. That missed every post-Scheduled
  //    in-progress / billing status (5 Ready to Dispatch, 6 In Transit,
  //    7 Checked In, 8 Checked Out, 9 COC Received, 10–13, 14 Billed,
  //    14.1, plus 30/30.1 Repair Open). The Optimizer's cell render
  //    has no statusCode filter — it shows ANY tile with non-empty
  //    titles. The dashboard MUST match because Brandon was correctly
  //    seeing in-progress (sc=7) multi-day jobs on tomorrow's cell in
  //    the Optimizer while the dashboard still listed those installers
  //    as openings. Confirmed via PROD Time Machine forensic for
  //    installers 0827.01 (BHM, 3 sc=7 jobs) and 1990.01 (ATL, 1 sc=7
  //    AUG cross-location job). Fix: exclude only statusCode 20
  //    (cancelled), matching the production filteredProjectsList rule
  //    in schedule.context.js.
  for (const p of Array.isArray(projects) ? projects : []) {
    if (!p) continue;
    const status = parseFloat(p.statusCode);
    if (status === 20) continue;
    if (!p.installer && p.installer !== 0) continue;
    if (!p.date_scheduled) continue;
    const startMs = new Date(p.date_scheduled).getTime();
    if (Number.isNaN(startMs)) continue;
    const endRaw = p.date_estimatedCompletion || p.date_scheduled;
    const endMs = new Date(endRaw).getTime();
    if (Number.isNaN(endMs)) continue;
    if (tomorrowMs < startMs || tomorrowMs > endMs) continue;
    scheduleArray.push({
      installer: payrollFormat(p.installer),
      // location intentionally omitted — shouldHighlightEmptyTomorrowCell's
      // job check doesn't filter on location (matches schedule.context.js
      // line 1893 comment). Including it would be inert but misleading.
      start: p.date_scheduled,
      end: endRaw,
    });
  }

  // 2. Attribute docs — daily AND default-pattern materialization.
  for (const a of Array.isArray(attributes) ? attributes : []) {
    if (!a) continue;
    if (!a._kf_payrollNo && a._kf_payrollNo !== 0) continue;
    const installer = payrollFormat(a._kf_payrollNo);
    const attrLocation = a.location;

    // 2a. Daily attribute on tomorrow's date.
    if (a.date && new Date(a.date).getTime() === tomorrowMs) {
      scheduleArray.push({
        attribute: true,
        installer,
        location: attrLocation,
        start: tomorrowDateString,
        encodedSmallTitle: a.encodedSmallTitle ?? "",
        encodedLargeTitle: a.encodedLargeTitle ?? "",
      });
      continue;
    }

    // 2b. Default-pattern attribute (date === "" and an availability array).
    //     Materialize into a per-weekday doc for tomorrow's weekday if the
    //     default specifies one. Mirrors schedule.context.js:1382-1505.
    if (
      (a.date === "" || a.date === null || a.date === undefined) &&
      Array.isArray(a.availability)
    ) {
      const dayEntry = a.availability.find((d) => d && d[tomorrowWeekdayKey]);
      if (dayEntry) {
        const status = dayEntry[tomorrowWeekdayKey];
        scheduleArray.push({
          attribute: true,
          installer,
          location: attrLocation,
          start: tomorrowDateString,
          encodedSmallTitle: status,
          encodedLargeTitle: status,
        });
      }
    }
  }

  // 3. Compliance-hold docs — derived from candidate's
  //    Scheduling_Hold_Effective_Date. Mirrors schedule.context.js:1617-1647.
  for (const c of Array.isArray(candidates) ? candidates : []) {
    if (!c) continue;
    if (!c.Scheduling_Hold_Effective_Date) continue;
    const effectiveMs = new Date(c.Scheduling_Hold_Effective_Date).getTime();
    if (Number.isNaN(effectiveMs)) continue;
    if (tomorrowMs < effectiveMs) continue;
    scheduleArray.push({
      attribute: true,
      complianceHold: true,
      installer: payrollFormat(c.payrollNo),
      location: c.cLocationCode,
      start: tomorrowDateString,
      encodedSmallTitle: "🔥 compliance hold 🔥",
      encodedLargeTitle: "🔥 compliance hold 🔥",
      locked: true,
    });
  }

  return scheduleArray;
}

// - - - - - - - - - - per-location aggregation

/**
 * For an opening installer (cell qualified for the red highlight), return a
 * display-friendly comma-joined string of any attribute markers present on
 * the cell that DIDN'T suppress the highlight — e.g. "BUFFER", "TRAINING",
 * default-pattern non-OFF values, or anything custom the scheduler typed.
 *
 * Returns "" when the cell is truly blank (no attribute doc for this
 * installer on tomorrow). The caller decides how to render the blank case
 * (em-dash, blank, etc.).
 *
 * NOTE: OFF and compliance-hold docs are EXCLUDED here because those
 * suppress the opening entirely — they cannot appear next to an opening
 * row. Filtering them out is defense-in-depth in case the upstream
 * `shouldHighlightEmptyTomorrowCell` semantics ever change.
 */
function getCellContentForInstaller({
  scheduleArray,
  payrollNo,
  locationFilter,
  tomorrowDateString,
}) {
  if (!Array.isArray(scheduleArray)) return "";
  const titles = [];
  for (const doc of scheduleArray) {
    if (!doc) continue;
    if (doc.attribute !== true) continue;
    if (doc.installer !== payrollNo) continue;
    if (doc.location !== locationFilter) continue;
    if (!datesEqual(doc.start, tomorrowDateString)) continue;
    if (doc.complianceHold === true) continue;
    if (doc.encodedSmallTitle === OFF_ATTRIBUTE_VALUE) continue;
    const title = doc.encodedSmallTitle;
    if (title === undefined || title === null || title === "") continue;
    titles.push(title);
  }
  return titles.join(", ");
}

// Replicates the two-step sort applied to the Optimizer's
// `filteredCandidatesList` (see modelTS.context.js `getCandidatesByCompany`
// select callback ~line 2247): primary `payrollNo` ascending as a number,
// secondary `Scheduling_Surface` descending as text. The descending text
// sort puts "Soft Surface" before "Hard Surface" — same upper-half /
// lower-half visual grouping as the grid's installer rows.
//
// Source of truth: modelTS.context.js's myJsonArraySort calls. If that
// canonical order ever changes, update this mirror in lockstep.
function sortCandidatesGridOrder(candidates) {
  // Don't mutate the caller's array. JS .sort is in-place; copy first.
  return candidates.slice().sort((a, b) => {
    // Primary: payrollNo ASC as numbers
    const numA = Number(a?.payrollNo);
    const numB = Number(b?.payrollNo);
    const aNaN = Number.isNaN(numA);
    const bNaN = Number.isNaN(numB);
    if (aNaN && bNaN) {
      // both unparseable — fall through to secondary
    } else if (aNaN) {
      return 1; // NaN sorts after numbers
    } else if (bNaN) {
      return -1;
    } else if (numA !== numB) {
      return numA - numB;
    }
    // Secondary: Scheduling_Surface DESC as text (case-aware via localeCompare)
    const surfA = String(a?.Scheduling_Surface ?? "");
    const surfB = String(b?.Scheduling_Surface ?? "");
    return surfB.localeCompare(surfA, undefined, { caseFirst: "upper" });
  });
}

// Surface-filter constants. Persisted as Users.dashboardSurfaceFilter in
// MongoDB (default "both" when the field is missing — no seed migration
// needed). Exported so the modal + payload builder can validate against
// the same source of truth.
export const DASHBOARD_SURFACE_FILTER_VALUES = Object.freeze([
  "soft",
  "hard",
  "both",
]);
export const DASHBOARD_SURFACE_FILTER_DEFAULT = "both";

/**
 * Filter a candidates list by Scheduling_Surface value. Pure.
 *
 *   "soft" → only candidates with Scheduling_Surface === "Soft Surface"
 *   "hard" → only candidates with Scheduling_Surface === "Hard Surface"
 *   "both" → no filter (input returned as-is, no copy)
 *   any other value → falls back to "both" (no filter)
 *
 * Returns the SAME array reference when no filter applies so downstream
 * memoization / identity checks remain stable.
 */
export function filterCandidatesBySurface(candidates, surfaceFilter) {
  if (!Array.isArray(candidates)) return [];
  if (surfaceFilter === "soft") {
    return candidates.filter((c) => c?.Scheduling_Surface === "Soft Surface");
  }
  if (surfaceFilter === "hard") {
    return candidates.filter((c) => c?.Scheduling_Surface === "Hard Surface");
  }
  return candidates;
}

/**
 * Returns the list of installers at this location whose tomorrow cell
 * would qualify for #138's red highlight (i.e., genuinely-empty openings).
 * Sorted to match the Optimizer grid's installer-row order.
 *
 * Each installer object:
 *   {
 *     payrollNo:         formatted, matches the grid's display payroll string
 *     name:              c.Name_Full (canonical, matches the grid's renderCrewCell)
 *                        with fallback chain for legacy / projection variants
 *     homeLocation:      c.cLocationCode or this location
 *     schedulingSurface: e.g. "Soft Surface" / "Hard Surface"
 *     cellContent:       "" if truly blank, else comma-joined attribute titles
 *                        (e.g. "BUFFER", "TRAINING") present on the cell that
 *                        didn't suppress the opening
 *   }
 *
 * `surfaceFilter` (optional, default "both") restricts the candidate set
 * to one half of the grid. The scheduleArray is STILL built from every
 * candidate at this location, so cross-installer attributes / projects
 * are present for context — but only filtered candidates are evaluated
 * for openings and appear in the result.
 */
export function buildLocationOpenings({
  locationCode,
  candidates,
  attributes,
  projects,
  tomorrowDateString,
  surfaceFilter = DASHBOARD_SURFACE_FILTER_DEFAULT,
}) {
  if (!tomorrowDateString || !locationCode) return [];
  if (!Array.isArray(candidates)) return [];

  const scheduleArray = buildTomorrowScheduleArray({
    locationCode,
    candidates,
    attributes,
    projects,
    tomorrowDateString,
  });

  const filteredCandidates = filterCandidatesBySurface(
    candidates,
    surfaceFilter
  );

  const filtered = filteredCandidates.filter((c) => {
    if (!c) return false;
    const payroll = payrollFormat(c.payrollNo);
    if (!payroll) return false;
    return shouldHighlightEmptyTomorrowCell({
      dateString: tomorrowDateString,
      nextBusinessDayDateString: tomorrowDateString,
      scheduleArray,
      payrollNo: payroll,
      locationFilter: locationCode,
    });
  });

  const sorted = sortCandidatesGridOrder(filtered);

  return sorted.map((c) => {
    const payroll = payrollFormat(c.payrollNo);
    return {
      payrollNo: payroll,
      name:
        c.Name_Full ||
        c.Calc_Name_Full ||
        c.schedulingName ||
        c.Calc_Installer_Payroll_Name ||
        "(unknown)",
      homeLocation: c.cLocationCode || locationCode,
      schedulingSurface: c.Scheduling_Surface || "",
      cellContent: getCellContentForInstaller({
        scheduleArray,
        payrollNo: payroll,
        locationFilter: locationCode,
        tomorrowDateString,
      }),
    };
  });
}

// - - - - - - - - - - partial-day capacity summary + openings (#192)

/**
 * Round a numeric capacity value for compact display. Defensive against
 * NaN / non-numeric input (returns 0).
 */
function fmtCap(n) {
  const v = Math.round(Number(n));
  return Number.isNaN(v) ? 0 : v;
}

/**
 * One-line summary of a carpet capacityInfo's remaining room, in yards.
 * Mirrors the popover's "Next job: up to X yds" framing (uses effectiveCap,
 * the next-slot cap). Falls back to a "carpet repair" hint when the ONLY
 * room left is repair-stops (#171 repair-only cells).
 */
function summarizeCarpetRemaining(info) {
  if (!info) return "";
  if (info.hasYardagePartialCapacity && info.effectiveCap > 0) {
    return `${fmtCap(info.effectiveCap)} yds carpet`;
  }
  if (info.isStopsRepairsCapConfigured && info.remainingRepairStops > 0) {
    return "carpet repair";
  }
  return "";
}

/**
 * One-line summary of a hard-surface capacityInfo's remaining room, in
 * square feet, per type that can still take a job. Falls back to a
 * "hard repair" hint when only repair-stops remain.
 */
function summarizeHardRemaining(info) {
  if (!info || !Array.isArray(info.perType)) {
    if (info && info.isStopsRepairsCapConfigured && info.remainingRepairStops > 0) {
      return "hard repair";
    }
    return "";
  }
  const types = info.perType.filter(
    (t) => t && t.canTakeJob && t.effectiveCap > 0
  );
  if (types.length > 0) {
    return types.map((t) => `${fmtCap(t.effectiveCap)} sqft ${t.type}`).join(", ");
  }
  if (info.isStopsRepairsCapConfigured && info.remainingRepairStops > 0) {
    return "hard repair";
  }
  return "";
}

/**
 * Build a compact, human-readable "remaining capacity" summary string from a
 * capacityInfo object (carpet / hard / both — the shape returned by
 * computeCellCapacityInfo). Pure. Returns "" when nothing meaningful remains.
 *
 * Examples:
 *   "120 yds carpet"
 *   "200 sqft Vinyl Plank, 150 sqft Tile"
 *   "80 yds carpet · 200 sqft Tile"   (does-both installer)
 */
export function summarizePartialCapacity(capacityInfo) {
  if (!capacityInfo) return "";
  const parts = [];
  if (capacityInfo.isBothSurfaces) {
    const c = summarizeCarpetRemaining(capacityInfo.carpetInfo);
    const h = summarizeHardRemaining(capacityInfo.hardInfo);
    if (c) parts.push(c);
    if (h) parts.push(h);
  } else if (capacityInfo.isHardSurface) {
    const h = summarizeHardRemaining(capacityInfo);
    if (h) parts.push(h);
  } else {
    const c = summarizeCarpetRemaining(capacityInfo);
    if (c) parts.push(c);
  }
  return parts.join(" · ");
}

/**
 * #192 — Partial-day openings for one location.
 *
 * Returns the installers at this location who, TOMORROW, already have at
 * least one job assigned AND still have remaining FLOORING capacity (carpet
 * yards or hard-surface sqft). Per Brandon's #192 follow-up (2026-06-09),
 * repair-stops-only availability does NOT count as a partial-day opening —
 * so this gates on `hasYardagePartialCapacity`, not `hasPartialCapacity`.
 *
 * Eligibility DELEGATES to `shouldHighlightPartialCapacityCell` — the exact
 * helper the Optimizer grid uses — so the dashboard's partial-day list can
 * never drift from what the grid shows. The capacity math itself reads the
 * raw projects (activeQueueDocs) so it sees Materials[].total_yds and the
 * SKU line items; the projects Lambda projection includes both (verified).
 *
 * Mutually exclusive with `buildLocationOpenings` (full-day / empty): an
 * empty cell can never satisfy the "≥ 1 job assigned" partial precondition.
 *
 * Each entry:
 *   { payrollNo, name, homeLocation, schedulingSurface, remainingSummary }
 *
 * @returns {Array} sorted to match the Optimizer grid's installer-row order.
 */
export function buildLocationPartialOpenings({
  locationCode,
  candidates,
  attributes,
  projects,
  tomorrowDateString,
  todayDateString,
  surfaceFilter = DASHBOARD_SURFACE_FILTER_DEFAULT,
  furnitureSkuList,
  stepsSkuList,
}) {
  if (!tomorrowDateString || !todayDateString || !locationCode) return [];
  if (!Array.isArray(candidates)) return [];

  const scheduleArray = buildTomorrowScheduleArray({
    locationCode,
    candidates,
    attributes,
    projects,
    tomorrowDateString,
  });
  const activeQueueDocs = Array.isArray(projects) ? projects : [];

  const filteredCandidates = filterCandidatesBySurface(
    candidates,
    surfaceFilter
  );

  const infoByPayroll = new Map();
  const qualifying = [];
  for (const c of filteredCandidates) {
    if (!c) continue;
    const payroll = payrollFormat(c.payrollNo);
    if (!payroll) continue;
    const { capacityInfo } = shouldHighlightPartialCapacityCell({
      dateString: tomorrowDateString,
      todayDateString,
      candidate: c,
      scheduleArray,
      activeQueueDocs,
      locationFilter: locationCode,
      furnitureSkuList,
      stepsSkuList,
      normalizePayroll: payrollFormat,
    });
    // #192 follow-up (Brandon 2026-06-09): "Do not consider Repairs for Partial
    // Day Capacity availability in the Next Day Openings." Gate on
    // hasYardagePartialCapacity (FLOORING room — carpet yds / hard sqft) rather
    // than hasPartialCapacity (which also fires on repair-stops-only, the #171
    // repair-only case). hasYardagePartialCapacity is ALSO false when the
    // date/OFF/SHARED gates fail (the helper returns its empty shape), so this
    // subsumes the prior shouldHighlight check.
    if (!capacityInfo || !capacityInfo.hasYardagePartialCapacity) continue;
    infoByPayroll.set(payroll, capacityInfo);
    qualifying.push(c);
  }

  return sortCandidatesGridOrder(qualifying).map((c) => {
    const payroll = payrollFormat(c.payrollNo);
    return {
      payrollNo: payroll,
      name:
        c.Name_Full ||
        c.Calc_Name_Full ||
        c.schedulingName ||
        c.Calc_Installer_Payroll_Name ||
        "(unknown)",
      homeLocation: c.cLocationCode || locationCode,
      schedulingSurface: c.Scheduling_Surface || "",
      remainingSummary: summarizePartialCapacity(infoByPayroll.get(payroll)),
    };
  });
}

// - - - - - - - - - - top-level dashboard build

/**
 * Build the dashboard's full per-location openings structure.
 *
 * @param {Object} params
 * @param {Object} params.dataByLocation       — output of useDashboardOpenings
 * @param {string} params.tomorrowDateString   — next-business-day en-us locale string
 * @param {string} [params.todayDateString]    — today's en-us locale string. #192:
 *                                               required to compute partial-day
 *                                               openings (the strictly-future gate
 *                                               needs "today"). Omitted → partial
 *                                               list is empty (no behavior change).
 * @param {string} [params.surfaceFilter]      — "soft" | "hard" | "both" (default "both")
 * @param {Array}  [params.furnitureSkuList]   — JOB_FURNITURE_SKULIST (forwarded to
 *                                               the partial-capacity engine).
 * @param {Array}  [params.stepsSkuList]       — JOB_STEPS_SKULIST (forwarded to the
 *                                               partial-capacity engine).
 * @returns {{
 *   locations: Array<{
 *     locationCode: string,
 *     openings: Array<{ payrollNo, name, homeLocation, schedulingSurface, cellContent }>,
 *     partialOpenings: Array<{ payrollNo, name, homeLocation, schedulingSurface, remainingSummary }>,
 *     isLoading: boolean,
 *     isError: boolean,
 *     candidatesCount: number,   // reflects surface filter
 *   }>,
 *   totalOpenings: number,
 *   totalPartialOpenings: number, // #192 — partial-day openings across locations
 *   totalCandidates: number,     // reflects surface filter
 *   degradedCount: number,       // #168 — locations EXCLUDED from the count
 *                                // because their data errored / came back
 *                                // silently-empty. When > 0 the total is
 *                                // partial and the UI must say so.
 * }}
 *
 * Each location object also carries `degraded` (boolean) and `degradeReason`
 * (string|null) so the per-location row can render a failure state instead
 * of a misleading "0 openings / ready".
 *
 * `candidatesCount` and `totalCandidates` reflect the SURFACE-FILTERED
 * candidate set — so when the user picks "Soft" only, "/ 18 installers"
 * displays the soft-installer count at that location, not the raw 29.
 */
export function buildDashboardOpenings({
  dataByLocation,
  tomorrowDateString,
  todayDateString,
  surfaceFilter = DASHBOARD_SURFACE_FILTER_DEFAULT,
  furnitureSkuList,
  stepsSkuList,
}) {
  const locations = [];
  let totalOpenings = 0;
  let totalPartialOpenings = 0;
  let totalCandidates = 0;
  let degradedCount = 0;

  for (const locationCode of Object.keys(dataByLocation || {}).sort()) {
    const entry = dataByLocation[locationCode] || {};
    const candidates = extractCandidates(entry.candidates);
    const attributes = extractAttributes(entry.attributes);
    const projects = extractProjects(entry.projects);

    const filteredCandidates = filterCandidatesBySurface(
      candidates,
      surfaceFilter
    );

    // #168 — a location is in one of three states:
    //   loading  → pending; contributes 0 (not a failure, just not ready)
    //   degraded → errored / silent-empty projects; EXCLUDED from the count
    //              and surfaced so the total reads as partial, never as
    //              "everyone here is open" (the silent over-count bug)
    //   clean    → counted normally
    // isLoading is checked first so a first-fetch (all data still null) is
    // never mistaken for a degraded location.
    const isLoading = !!entry.isLoading;
    const { degraded, reason: degradeReason } = isLoading
      ? { degraded: false, reason: null }
      : assessLocationData(entry);

    const openings =
      isLoading || degraded
        ? []
        : buildLocationOpenings({
            locationCode,
            candidates,
            attributes,
            projects,
            tomorrowDateString,
            surfaceFilter,
          });

    // #192 — partial-day openings (installers with work + remaining capacity
    // tomorrow). Computed only when todayDateString is supplied; older callers
    // / tests that omit it get an empty partial list (no behavior change).
    const partialOpenings =
      isLoading || degraded || !todayDateString
        ? []
        : buildLocationPartialOpenings({
            locationCode,
            candidates,
            attributes,
            projects,
            tomorrowDateString,
            todayDateString,
            surfaceFilter,
            furnitureSkuList,
            stepsSkuList,
          });

    if (degraded) degradedCount++;

    // Per-surface breakdown so the location header line can split counts
    // when the user has "both" surfaces selected. Counts are computed over
    // the post-filter set: when filter is "soft", hardCount fields are 0
    // (and vice versa); when filter is "both" both are populated naturally.
    const softCandidatesCount = filteredCandidates.filter(
      (c) => c?.Scheduling_Surface === "Soft Surface"
    ).length;
    const hardCandidatesCount = filteredCandidates.filter(
      (c) => c?.Scheduling_Surface === "Hard Surface"
    ).length;
    const softOpeningsCount = openings.filter(
      (o) => o.schedulingSurface === "Soft Surface"
    ).length;
    const hardOpeningsCount = openings.filter(
      (o) => o.schedulingSurface === "Hard Surface"
    ).length;
    const softPartialOpeningsCount = partialOpenings.filter(
      (o) => o.schedulingSurface === "Soft Surface"
    ).length;
    const hardPartialOpeningsCount = partialOpenings.filter(
      (o) => o.schedulingSurface === "Hard Surface"
    ).length;

    locations.push({
      locationCode,
      openings,
      partialOpenings,
      degraded,
      degradeReason,
      isLoading: !!entry.isLoading,
      // isFetching propagated through so the LocationGroup status
      // badge can show "refreshing…" on per-location rows during a
      // user-initiated refresh or a close-reopen refetch. Without it,
      // only the modal-level status badge would update and individual
      // rows would silently show stale numbers + a green "ready"
      // appearance during the refresh.
      isFetching: !!entry.isFetching,
      isError: !!entry.isError,
      candidatesCount: filteredCandidates.length,
      softCandidatesCount,
      hardCandidatesCount,
      softOpeningsCount,
      hardOpeningsCount,
      softPartialOpeningsCount,
      hardPartialOpeningsCount,
    });

    totalOpenings += openings.length;
    totalPartialOpenings += partialOpenings.length;
    totalCandidates += filteredCandidates.length;
  }

  return {
    locations,
    totalOpenings,
    totalPartialOpenings,
    totalCandidates,
    degradedCount,
  };
}
